import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';

import { ButtonChip } from '@domains/chips';
import { Recipe, RecipeRubric } from '@domains/recipes';

import { ALL_RECIPES_CHIP_BASE, ALL_RECIPES_CHIP_ID } from '@utils/constants';
import { isValueSet } from '@utils/guards';
import { makeRecipesRoute } from '@utils/route';
import { applyTypograf } from '@utils/typograf';

import { useEmbla } from '@hooks/use-embla';

import { Chips, ChipsKind } from '@components/common/Chip/Chips';
import { Container } from '@components/common/Container/Container';
import { RecipeCard } from '@components/common/RecipeCard/RecipeCard';
import { SliderBlockTitle } from '@components/common/SliderBlockTitle/SliderBlockTitle';
import { SliderNavigation } from '@components/common/SliderNavigation/SliderNavigation';
import { SliderPagination } from '@components/common/SliderPagination/SliderPagination';

import styles from './RecipesSlider.module.scss';

export const mapRubricsToButtonChips = (
    rubrics: RecipeRubric[],
    onClick: (slug: string) => void,
): ButtonChip[] =>
    rubrics.map((rubric) => ({
        id: rubric.slug,
        caption: rubric.name,
        onClick: () => onClick(rubric.slug),
    }));

export type ClassKey = 'pagination';

export type RecipesSliderProps = {
    recipes: Recipe[];
    rubrics?: RecipeRubric[];
    title?: string;
    isWithChips?: boolean;
    isWithTitleLink?: boolean;
    isWithPagination?: boolean;
    className?: string;
    classes?: Classes<ClassKey>;
};
export const RecipesSlider: FunctionComponent<RecipesSliderProps> = (props) => {
    const {
        recipes,
        rubrics,
        className,
        classes,
        title = 'Рецепты с этим товаром',
        isWithTitleLink = false,
        isWithChips = false,
        isWithPagination = true,
    } = props;

    const {
        scrollPrev,
        scrollNext,
        scrollSnaps,
        scrollTo,
        selectedIndex,
        isPrevButtonEnabled,
        isNextButtonEnabled,
        viewportRef,
        emblaApi,
    } = useEmbla({
        loop: false,
        startIndex: 0,
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
        align: 'start',
    });

    // Chips
    const [activeChipId, setActiveChipId] = useState<string>(ALL_RECIPES_CHIP_ID);

    const allRecipesChip: ButtonChip = {
        ...ALL_RECIPES_CHIP_BASE,
        onClick: () => setActiveChipId(ALL_RECIPES_CHIP_ID),
    };
    const chipsData: ButtonChip[] | null = isValueSet(rubrics)
        ? [allRecipesChip, ...mapRubricsToButtonChips(rubrics, setActiveChipId)]
        : null;

    // Slides
    const formattedSlides: Recipe[] = useMemo(() => {
        if (activeChipId === ALL_RECIPES_CHIP_ID) return recipes;

        return recipes.filter((recipe) =>
            recipe.rubrics.some((recipeRubric) => recipeRubric.slug === activeChipId),
        );
    }, [activeChipId, recipes]);

    // Restart the slider when the chip is switched, and rewind to the beginning
    useEffect(() => {
        if (isValueSet(emblaApi)) {
            emblaApi.reInit();
            emblaApi.scrollTo(0);
        }
    }, [formattedSlides, emblaApi]);

    return (
        <div className={clsx(styles.root, className)}>
            <SliderBlockTitle
                title={title}
                link={isWithTitleLink ? makeRecipesRoute() : undefined}
                linkText="Смотреть все"
            />

            {isWithChips && chipsData !== null && (
                <Chips
                    kind={ChipsKind.Buttons}
                    data={chipsData}
                    activeChipId={activeChipId}
                    className={styles.chips}
                />
            )}

            <Container classes={{ root: styles.container }}>
                <div className={styles.slider}>
                    <div ref={viewportRef} className={styles.viewport}>
                        <div className={styles.sliderContainer}>
                            {formattedSlides.map((recipe) => (
                                <RecipeCard
                                    key={recipe.slug}
                                    recipe={recipe}
                                    className={styles.slide}
                                />
                            ))}
                        </div>

                        {formattedSlides.length === 0 && (
                            <p className={styles.empty}>
                                {applyTypograf('Упс! Рецепты не найдены...')}
                            </p>
                        )}
                    </div>

                    <SliderNavigation
                        onPrev={scrollPrev}
                        onNext={scrollNext}
                        isPrevButtonEnabled={isPrevButtonEnabled}
                        isNextButtonEnabled={isNextButtonEnabled}
                        variation="circle"
                        classes={{ button: styles.navigationButton }}
                    />

                    {isWithPagination && formattedSlides.length > 0 && scrollSnaps.length > 1 && (
                        <SliderPagination
                            scrollSnaps={scrollSnaps}
                            scrollTo={scrollTo}
                            selectedIndex={selectedIndex}
                            classes={{ root: classes?.pagination }}
                        />
                    )}
                </div>
            </Container>
        </div>
    );
};
